import { useCallback, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { getSpendLensItems } from '@/api/clarityLens'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { IAbstractLensItem } from '@/types'
import { SpendLensItemsModel } from '@/types/clarityLens'
import { areFilterParamsValid } from '@/utils/filterParams'
import { dateToString } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useSpendLensItems(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () => areFilterParamsValid(startDate, endDate, branches),
    [startDate, endDate, branches]
  )
  const loading = !isReady || isFetching

  const [itemsData, setItemsData] = useState<
    Map<string, SpendLensItemsModel[]>
  >(new Map())
  const [trendItemsData, setTrendItemsData] = useState<
    Map<string, SpendLensItemsModel[]>
  >(new Map())

  const fetchItemsForCategory = useCallback(
    async (category: string): Promise<SpendLensItemsModel[]> => {
      if (!isReady) return []
      if (itemsData.has(category)) {
        return itemsData.get(category)!
      }
      setIsFetching(true)
      try {
        const [currentData, trendData] = await Promise.all([
          fetchItemsData(branches, startDate, endDate, category),
          fetchTrendItemsData(branches, category),
        ])

        // Update the cache with new data
        setItemsData((prev) => new Map(prev.set(category, currentData.data)))
        setTrendItemsData((prev) => new Map(prev.set(category, trendData.data)))

        return currentData.data
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred'
        const error = new Error(errorMessage)

        dispatch(
          pushNotification({
            type: 'error',
            title: t('common.errors.serverError'),
            description: errorMessage,
            duration: 5000,
          })
        )
        throw error
      } finally {
        setIsFetching(false)
      }
    },
    [branches, startDate, endDate, itemsData, isReady]
  )

  const fetchTrendItemsData = useCallback(
    async (
      branches: string,
      category: string
    ): Promise<{ data: SpendLensItemsModel[] }> => {
      const trendingDate = getTrendingDate(
        { from: new Date(startDate), to: new Date(endDate) },
        dateFilterType
      ) as DateRange
      return await fetchItemsData(
        branches,
        dateToString(trendingDate, 'from'),
        dateToString(trendingDate, 'to'),
        category
      )
    },
    []
  )

  const hasItemsForCategory = useCallback(
    (category: string): boolean => {
      return itemsData.has(category)
    },
    [itemsData]
  )

  const hasTrendItemsForCategory = useCallback(
    (category: string): boolean => {
      return trendItemsData.has(category)
    },
    [trendItemsData]
  )

  const getFormattedItemsForCategory = useCallback(
    (
      category: string
    ): Array<IAbstractLensItem<any> & { parentName?: string }> => {
      const items = itemsData.get(category) || []
      const trendItems = trendItemsData.get(category) || []

      // Create a map of trend data by item name for easy lookup
      const trendDataMap = new Map(
        trendItems.map((item) => [item.itemName, item])
      )

      return items.map(
        (
          item: SpendLensItemsModel
        ): IAbstractLensItem<any> & { parentName?: string } => {
          const trendItem = trendDataMap.get(item.itemName)

          return {
            item: item.itemName,
            parentName: item.subCategory, // Use subCategory from model
            current: {
              totalSpend: item.totalSpend,
              spendSharePercentage: item.spendSharePercentage,
              averageUnitPrice: item.averageUnitPrice,
              activeSkuCount: item.activeSkuCount,
            },
            prev: trendItem
              ? {
                  totalSpend: trendItem.totalSpend,
                  spendSharePercentage: trendItem.spendSharePercentage,
                  averageUnitPrice: trendItem.averageUnitPrice,
                  activeSkuCount: trendItem.activeSkuCount,
                }
              : undefined,
          }
        }
      )
    },
    [itemsData, trendItemsData]
  )

  const clearCache = useCallback(() => {
    setItemsData(new Map())
    setTrendItemsData(new Map())
  }, [])

  const refetch = useCallback(() => {
    console.log('Refetching spend lens items (clearing cache)...')
    clearCache()
  }, [clearCache])

  return {
    fetchItemsForCategory,
    hasItemsForCategory,
    hasTrendItemsForCategory,
    getFormattedItemsForCategory,
    clearCache,
    refetch,
    loading,
  }
}

async function fetchItemsData(
  branches: string,
  startDate: string,
  endDate: string,
  category: string
): Promise<{
  data: SpendLensItemsModel[]
}> {
  try {
    const response = await getSpendLensItems(
      branches,
      startDate,
      endDate,
      category
    )

    const data: SpendLensItemsModel[] = response.map((responseItem) => {
      return convertData(
        responseItem.item_name,
        responseItem.total_spend,
        responseItem.spend_share_percentage,
        responseItem.average_unit_price,
        responseItem.active_sku_count,
        category // Pass subcategory as parent
      )
    })

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  itemName: string,
  totalSpend: number,
  spendSharePercentage: number,
  averageUnitPrice: number,
  activeSkuCount: number,
  subCategory: string
): SpendLensItemsModel {
  return {
    itemName,
    subCategory,
    totalSpend: formatMetric(totalSpend, 2),
    spendSharePercentage: formatMetric(spendSharePercentage, 2),
    averageUnitPrice: formatMetric(averageUnitPrice, 2),
    activeSkuCount: formatMetric(activeSkuCount, 2),
  }
}
